import React from 'react';
import { Box, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { QcDiagnosticWorkupDto } from '../../../services/qualityCheck';
import DiagnosticWorkup from './DiagnosticWorkup';

interface VisitLevelDiagnosesProps {
    /** The legacy visit-level D1 instances, as served in `visitLevelDiagnoses`. */
    diagnoses: QcDiagnosticWorkupDto[];
}

/**
 * SKIN-611 — the diagnosis a visit recorded once, before SKIN-444 moved it per lesion.
 *
 * Its own block, labelled visit-level, and never rendered inside a lesion section: on a
 * visit with two diseases on two lesions, a visit-level final diagnosis shown under a
 * lesion would claim that lesion was diagnosed with something nobody recorded for it.
 * Renders nothing for a visit recorded per lesion.
 */
export default function VisitLevelDiagnoses({ diagnoses }: VisitLevelDiagnosesProps) {
    if (diagnoses.length === 0) return null;
    return (
        <Box
            data-testid="visit-level-diagnoses"
            sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}
        >
            <Typography variant="subtitle1" component="h5">
                <Translate id="hospital.quality-check.review.workup.visit-level-title" />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                <Translate id="hospital.quality-check.review.workup.visit-level-note" />
            </Typography>
            {diagnoses.map((workup, index) => (
                // The instances carry no identity of their own; their order is the record's.
                // eslint-disable-next-line react/no-array-index-key
                <Box key={index} sx={{ '& + &': { mt: 1.5 } }}>
                    {diagnoses.length > 1 && (
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            <Translate
                                id="hospital.quality-check.review.workup.visit-level-entry"
                                data={{ n: index + 1 }}
                            />
                        </Typography>
                    )}
                    <DiagnosticWorkup workup={workup} />
                </Box>
            ))}
        </Box>
    );
}
